import * as React from 'react'
import { NavDrawer, List, Accordion } from 'cs-kevlar-react'
import { NavLink } from './NavLink'
import { makeStyles, createStyles, Theme } from 'cs-kevlar-react/lib/es/theming/theme'
import { SelectedNavLink } from 'cs-kevlar-react/lib/es/theming/palette'
import { PageDefinition } from 'pages/pageDefinition'
import {matchPath, useLocation} from 'react-router-dom'
import { useAuth } from 'hooks'
import { removeUnauthorizedPages, doesPageMatchRoute } from 'utils/pages'
import { WebAdminUser } from '../../api'
import clsx from 'clsx'

interface StyleProps {
  currentTitle: string | null
  user: WebAdminUser | null
}

interface NavContainerProps {
  currentTitle: string | null
  topLevelPages: PageDefinition[]
  agencyPages: PageDefinition[]
  navigate: (page: PageDefinition) => void
  unassistAgency: () => void
  className: string
  paperClassName?: string
  open?: boolean
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawerPaper: {
      position: 'static',
      width: 240,
    },
    navItem: {
      color: theme.palette.neutral.contrastText,
      paddingTop: theme.spacing(7),
      paddingBottom: theme.spacing(6),
      margin: 0,
      width: '100%',
      '& .MuiListItemText-root': {
        margin: 0,
      },
      '&:hover': {
        // TODO: get background color from Rob
      },
    },
    assistedAgencyContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      marginLeft: theme.spacing(4),
      marginRight: theme.spacing(4),
      height: 50,
    },
    backButton: {
      color: theme.palette.neutral.contrastText,
    },
    assistedAgency: {
      fontWeight: 600,
      flexGrow: 1,
      minWidth: 0,
      color: theme.palette.neutral.contrastText,
      textAlign: 'center',
    },
    navList: {
      paddingTop: 0,
      paddingBottom: 0,
    },
    groupedNavAccordion: {
      '&.MuiAccordion-root:before': {
        display: 'none',
      },
      '& .MuiAccordionDetails-root': {
        padding: 0,
      },
      '& .MuiAccordionSummary-root': {
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      '& .MuiAccordionSummary-content': {
        borderRight: 'none',
        paddingTop: theme.spacing(7),
        paddingBottom: theme.spacing(6),
      },
      '& .MuiTypography-body1': {
        fontWeight: 400,
        fontSize: theme.spacing(8),
      }
    },
    groupedNavList: {
      padding: 0,
      width: '100%',
    },
    groupedNavItem: {
      color: theme.palette.neutral.contrastText,
      backgroundColor: theme.palette.background.default,
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),
      margin: 0,
      width: '100%',
      '& .MuiTypography-body1': {
        fontSize: theme.spacing(7),
      },
      '& .MuiListItemText-root': {
        margin: 0,
        marginLeft: theme.spacing(8),
      },
      '&:hover': {
        backgroundColor: theme.palette.background.default,
      },
    },
    selectedNavItem: {
      backgroundColor: SelectedNavLink,
      color: theme.palette.primary.main,
      borderLeft: `4px solid ${theme.palette.primary.light}`,
      paddingLeft: theme.spacing(6),
      paddingRight: theme.spacing(6),
      '&.MuiListItem-button:hover': {
        backgroundColor: SelectedNavLink,
      },
    },
  }),
)

export const NavContainer: React.FunctionComponent<NavContainerProps> = (
  props,
) => {
  const location = useLocation()
  const { user } = useAuth()
  const classes = useStyles({
    currentTitle: props.currentTitle,
    user,
  })

  const [authorizedTopLevelPages, setAuthorizedTopLevelPages] = React.useState<
  [string, PageDefinition[]][]
  >([])

  // re-calculate the authorized page list whenever the auth data changes
  React.useEffect(() => {
    setAuthorizedTopLevelPages(
      groupPages(removeUnauthorizedPages(props.topLevelPages, user)),
    )
  }, [user])

  const [authorizeAgencyPages, setAuthorizedAgencyPages] = React.useState<
  [string, PageDefinition[]][]
  >([])

  // re-calculate the authorized page list whenever the auth data changes
  React.useEffect(() => {
    setAuthorizedAgencyPages(
      groupPages(removeUnauthorizedPages(props.agencyPages, user))
    )
  }, [user])

  const [
    isRenderingAgencyAssistPage,
    setIsRenderingAgencyAssistPage,
  ] = React.useState<boolean>(
    doesPageMatchRoute('/assist/*', location.pathname, false),
  )

  React.useEffect(() => {
    setIsRenderingAgencyAssistPage(
      doesPageMatchRoute('/assist/*', location.pathname, false),
    )
  }, [location.pathname])

  const onAgencyAssistBackClicked = () => {
    setIsRenderingAgencyAssistPage(false)
  }

  const groupPages = (pages: PageDefinition[]) =>
  {
    const map = new Map<string, PageDefinition[]>();
    pages.forEach((item) => {
      let key = '';
      if(item.groupName != null && item.groupName.trim() !== '')
      {
        key = item.groupName
      }
      const collection = map.get(key);
      if (!collection) {
          map.set(key, [item]);
      } else {
          collection.push(item);
      }
    });

    return Array.from(map);
  }
  
  const shouldGroupBeInitiallyExpanded = (pages: PageDefinition[]) => {
    
    for(let page of pages) {
      for(let path of page.routes(user)) {
        const match = matchPath({
          path,
          caseSensitive: true
        }, location.pathname)
        
        if(match) return true;
      }
    }
    
    return false;
  }

  return (
    <React.Fragment>
      <NavDrawer
        className={props.className}
        paperClassName={clsx(classes.drawerPaper, props.paperClassName)}
        open={props.open}
      >
        <List className={classes.navList}>
          {!isRenderingAgencyAssistPage &&
            authorizedTopLevelPages.map(([groupName, pages]) => {
              if(groupName === '')
              {
                return pages.map((page) => {                
                  return <NavLink
                    key={page.navLinkText}
                    page={page}
                    location={location}
                    className={classes.navItem}
                    selectedClassName={classes.selectedNavItem}
                    navigate={props.navigate}
                    user={user}
                  />
                })
              }
              else
              {
                return <Accordion
                  id="NavLinks"
                  title={groupName}
                  className={classes.groupedNavAccordion}
                  initiallyExpanded={shouldGroupBeInitiallyExpanded(pages)}>
                    <List className={classes.groupedNavList}>
                      {pages.map((page) => (
                        <NavLink
                          key={page.navLinkText}
                          page={page}
                          location={location}
                          className={classes.groupedNavItem}
                          selectedClassName={classes.selectedNavItem}
                          navigate={props.navigate}
                          user={user}
                        />
                      ))}
                    </List>
                  </Accordion>
              }                
            })}
          {!isRenderingAgencyAssistPage &&
            !user?.customerAgencyId &&
            authorizeAgencyPages.map(([groupName, pages]) => {
              if(groupName === '')
              {
                return pages.map((page) => {
                  return <NavLink
                    key={page.navLinkText}
                    page={page}
                    location={location}
                    className={classes.navItem}
                    selectedClassName={classes.selectedNavItem}
                    navigate={props.navigate}
                    user={user}
                  />
                })
              }
              else
              {
                return <Accordion
                  id="NavLinks"
                  title={groupName}
                  className={classes.groupedNavAccordion}
                  initiallyExpanded={shouldGroupBeInitiallyExpanded(pages)}>
                    <List className={classes.groupedNavList}>
                      {pages.map((page) => (
                        <NavLink
                          key={page.navLinkText}
                          page={page}
                          location={location}
                          className={classes.groupedNavItem}
                          selectedClassName={classes.selectedNavItem}
                          navigate={props.navigate}
                          user={user}
                        />
                      ))}
                    </List>
                  </Accordion>
              }                
            })}
        </List>
        {isRenderingAgencyAssistPage && !!user?.licensedAgencyName && (
          <React.Fragment>
            <List className={classes.navList}>
              {authorizeAgencyPages.map(([groupName, pages]) => {
                if(groupName === '')
                {
                  return pages.map((page) => {                
                    return <NavLink
                      key={page.navLinkText}
                      page={page}
                      location={location}
                      className={classes.navItem}
                      selectedClassName={classes.selectedNavItem}
                      navigate={props.navigate}
                      user={user}
                    />
                  })
                }
                else
                {
                  return <Accordion
                    id="NavLinks"
                    title={groupName}
                    className={classes.groupedNavAccordion}
                    initiallyExpanded={shouldGroupBeInitiallyExpanded(pages)}>
                      <List className={classes.groupedNavList}>
                        {pages.map((page) => (
                          <NavLink
                            key={page.navLinkText}
                            page={page}
                            location={location}
                            className={classes.groupedNavItem}
                            selectedClassName={classes.selectedNavItem}
                            navigate={props.navigate}
                            user={user}
                          />
                        ))}
                      </List>
                    </Accordion>
                }                
              })}
            </List>
          </React.Fragment>
        )}
      </NavDrawer>
    </React.Fragment>
  )
}
